import { useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { AuthLayout } from '@/components/templates/AuthLayout';
import { FormField } from '@/components/molecules/FormField';
import { Checkbox } from '@/components/atoms/Checkbox';
import { Button } from '@/components/atoms/Button';
import { useAuth } from '@/hooks/useAuth';
import { createRegisterSchema, type RegisterFormData } from '@/utils/validation/auth.schemas';
import { ROUTES } from '@/constants/routes';
import styles from './RegisterPage.module.scss';

export function RegisterPage() {
  const { t } = useTranslation();
  const { register: registerUser } = useAuth();
  const navigate = useNavigate();

  const registerSchema = useMemo(() => createRegisterSchema(t), [t]);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      acceptedTerms: false,
    },
  });

  const onSubmit = async (data: RegisterFormData) => {
    try {
      await registerUser(data);
      navigate(ROUTES.STUDENT.DASHBOARD, { replace: true });
    } catch (error) {
      setError('root', {
        type: 'manual',
        message: error instanceof Error ? error.message : t('auth.errors.registerFailed'),
      });
    }
  };

  return (
    <AuthLayout title={t('auth.register.title')} subtitle={t('auth.register.subtitle')}>
      <form className={styles.registerForm} onSubmit={handleSubmit(onSubmit)}>
        {errors.root && (
          <div className={styles.registerForm__error} role="alert">
            {errors.root.message}
          </div>
        )}

        <FormField
          label={t('auth.register.fullNameLabel')}
          type="text"
          placeholder={t('auth.register.fullNamePlaceholder')}
          error={errors.name?.message}
          required
          autoComplete="name"
          {...register('name')}
        />

        <FormField
          label={t('auth.register.emailLabel')}
          type="email"
          placeholder={t('auth.register.emailPlaceholder')}
          error={errors.email?.message}
          required
          autoComplete="email"
          {...register('email')}
        />

        <FormField
          label={t('auth.register.companyCodeLabel')}
          type="text"
          placeholder={t('auth.register.companyCodePlaceholder')}
          error={errors.companyCode?.message}
          helperText={t('auth.register.companyCodeHelper')}
          required
          autoComplete="off"
          {...register('companyCode')}
        />

        <FormField
          label={t('auth.register.passwordLabel')}
          type="password"
          placeholder={t('auth.register.passwordPlaceholder')}
          error={errors.password?.message}
          helperText={t('auth.register.passwordHelper')}
          required
          autoComplete="new-password"
          {...register('password')}
        />

        <FormField
          label={t('auth.register.confirmPasswordLabel')}
          type="password"
          placeholder={t('auth.register.confirmPasswordPlaceholder')}
          error={errors.confirmPassword?.message}
          required
          autoComplete="new-password"
          {...register('confirmPassword')}
        />

        <div>
          <Controller
            name="acceptedTerms"
            control={control}
            render={({ field }) => (
              <Checkbox
                label={
                  <span>
                    {t('auth.register.termsPrefix')}{' '}
                    <a
                      href={ROUTES.LEGAL.TERMS}
                      className={styles.registerForm__link}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('auth.register.termsLink')}
                    </a>
                  </span>
                }
                id="acceptedTerms"
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            )}
          />
          {errors.acceptedTerms && (
            <p className={styles.registerForm__error} role="alert">
              {errors.acceptedTerms.message}
            </p>
          )}
        </div>

        <Button type="submit" variant="primary" size="lg" fullWidth isLoading={isSubmitting}>
          {t('auth.register.submit')}
        </Button>

        <p className={styles.registerForm__footer}>
          {t('auth.register.hasAccount')}{' '}
          <Link to={ROUTES.AUTH.LOGIN} className={styles.registerForm__link}>
            {t('auth.register.signIn')}
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}

export default RegisterPage;
